// Entity response shapes. API §1.2 + architecture.md §5 additions.
import { z } from 'zod';
import {
  contributionStatus,
  cycleStatus,
  departmentStatus,
  departmentType,
  devicePlatform,
  editionStatus,
  membershipRole,
  membershipStatus,
  notificationType,
  occasionRecurrence,
  occasionSubjectType,
  occasionType,
  promptType,
  reactionTargetType,
  reactionType,
  textSizePref,
  themeSource,
} from './enums.js';

export const uuid = z.string().uuid();
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export const yearMonth = z.string().regex(/^\d{4}-\d{2}$/, 'Expected YYYY-MM');
export const isoDateTime = z.string().datetime({ offset: true });

export const userSchema = z.object({
  id: uuid,
  name: z.string(),
  email: z.string(),
  auth_method: z.enum(['google', 'email']),
  avatar_url: z.string().nullable(),
  birthday: isoDate.nullable(),
  city: z.string().nullable(),
  text_size_pref: textSizePref,
  created_at: isoDateTime,
});
export type User = z.infer<typeof userSchema>;

export const familySummarySchema = z.object({
  id: uuid,
  name: z.string(),
  member_count: z.number().int(),
  my_role: membershipRole,
  my_status: membershipStatus,
  current_cycle_status: cycleStatus.nullable(),
  timezone: z.string(),
  created_at: isoDateTime,
});
export type FamilySummary = z.infer<typeof familySummarySchema>;

export const memberSchema = z.object({
  user_id: uuid,
  name: z.string(),
  avatar_url: z.string().nullable(),
  role: membershipRole,
  status: membershipStatus,
  birthday: isoDate.nullable(),
  joined_at: isoDateTime,
  archive_access_from: isoDate.nullable(),
});
export type Member = z.infer<typeof memberSchema>;

export const petSchema = z.object({
  id: uuid,
  family_id: uuid,
  name: z.string(),
  species: z.string(),
  avatar_url: z.string().nullable(),
  birthday: isoDate.nullable(),
  created_by: uuid,
});
export type Pet = z.infer<typeof petSchema>;

export const cycleSchema = z.object({
  id: uuid,
  family_id: uuid,
  year_month: yearMonth,
  theme: z.string().nullable(),
  theme_source: themeSource.nullable(),
  status: cycleStatus,
  editor_user_id: uuid.nullable(),
  window_start: isoDateTime,
  cutoff: isoDateTime,
  drop_target: isoDateTime,
});
export type Cycle = z.infer<typeof cycleSchema>;

export const promptSchema = z.object({
  id: uuid,
  cycle_id: uuid,
  text: z.string(),
  type: promptType,
  created_at: isoDateTime,
});
export type Prompt = z.infer<typeof promptSchema>;

export const photoSchema = z.object({
  id: uuid,
  contribution_id: uuid,
  storage_key: z.string(),
  cdn_url: z.string(),
  display_order: z.number().int().min(1).max(5),
  alt_text: z.string().nullable(),
  mime_type: z.string(),
  size_bytes: z.number().int(),
});
export type Photo = z.infer<typeof photoSchema>;

export const contributionSchema = z.object({
  id: uuid,
  user_id: uuid,
  prompt_id: uuid,
  cycle_id: uuid,
  family_id: uuid,
  text_body: z.string().nullable(),
  status: contributionStatus,
  photos: z.array(photoSchema),
  submitted_at: isoDateTime.nullable(),
  updated_at: isoDateTime,
});
export type Contribution = z.infer<typeof contributionSchema>;

export const occasionSchema = z.object({
  id: uuid,
  family_id: uuid,
  subject_type: occasionSubjectType,
  subject_id: uuid,
  subject_name: z.string(),
  subject_avatar: z.string().nullable(),
  occasion_type: occasionType,
  title: z.string(),
  date: isoDate,
  recurrence: occasionRecurrence,
  days_until: z.number().int(),
  created_by: uuid,
});
export type Occasion = z.infer<typeof occasionSchema>;

export const reactionSchema = z.object({
  id: uuid,
  edition_id: uuid,
  user_id: uuid,
  target_type: reactionTargetType,
  target_id: z.string(),
  type: reactionType,
});
export type Reaction = z.infer<typeof reactionSchema>;

export const notificationSchema = z.object({
  id: uuid,
  type: notificationType,
  family_id: uuid.nullable(),
  family_name: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  payload_json: z.object({ deep_link: z.string(), metadata: z.record(z.unknown()).optional() }),
  is_read: z.boolean(),
  created_at: isoDateTime,
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationPreferenceSchema = z.object({
  type: notificationType,
  enabled: z.boolean(),
  system_critical: z.boolean(),
});

export const deviceSchema = z.object({
  id: uuid,
  platform: devicePlatform,
  last_seen_at: isoDateTime,
});

export const departmentSchema = z.object({
  type: departmentType,
  status: departmentStatus,
  content_json: z.unknown().nullable(),
});
export type Department = z.infer<typeof departmentSchema>;

export const editionSummarySchema = z.object({
  id: uuid,
  family_id: uuid,
  family_name: z.string(),
  year_month: yearMonth,
  theme: z.string().nullable(),
  status: editionStatus,
  cover: z.object({ theme: z.string().nullable(), month_label: z.string() }),
  compiled_at: isoDateTime.nullable(),
  locked: z.boolean(), // late-joiner archive lock, App Flow §4
  is_new: z.boolean(), // dropped within 7 days
});
export type EditionSummary = z.infer<typeof editionSummarySchema>;

export const paginationSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
  has_more: z.boolean(),
});
